import { message } from 'antd';
import { useCallback, useState } from 'react';

import { createKnowledgeNote } from '@/api/knowledge/api';
import { type ChatHistoryItem } from '@/hooks/useChatFooter';
import { generateUuidV4 } from '@/utils/uuid';

export interface UseNoteSavingOptions {
  accessToken: string;
  clientId: string;
  projectId: string;
  onCreateNoteNavigate?: (assetSessionId: string) => void;
}

export interface UseNoteSavingResult {
  savedNoteIds: Set<number>;
  savingNoteId: number | null;
  onSaveAsNote: (item: ChatHistoryItem) => Promise<void>;
}

/**
 * Manages saving chat assistant responses as knowledge notes.
 * Self-contained: no dependency on provider or chat streaming state.
 */
export function useNoteSaving({
  accessToken,
  clientId,
  projectId,
  onCreateNoteNavigate,
}: UseNoteSavingOptions): UseNoteSavingResult {
  const [savedNoteIds, setSavedNoteIds] = useState<Set<number>>(new Set());
  const [savingNoteId, setSavingNoteId] = useState<number | null>(null);

  const onSaveAsNote = useCallback(
    async (item: ChatHistoryItem) => {
      setSavingNoteId(item.id);
      try {
        const sessionId = generateUuidV4();
        const title = `Chat Note – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        await createKnowledgeNote(accessToken, clientId, {
          project_id: projectId,
          card_id: sessionId,
          title,
          markdown_content: item.text,
          tags: [],
        });
        setSavedNoteIds((previous) => new Set(previous).add(item.id));
        if (onCreateNoteNavigate) {
          onCreateNoteNavigate(sessionId);
        } else {
          void message.success('Response saved as note');
        }
      } catch {
        void message.error('Failed to save note');
      } finally {
        setSavingNoteId(null);
      }
    },
    [accessToken, clientId, projectId, onCreateNoteNavigate],
  );

  return { savedNoteIds, savingNoteId, onSaveAsNote };
}
